import { Trans } from "@lingui/macro";
import React, { useEffect, useState } from "react";
import { ProductItem } from "../Products/ProductItem";
import { useInputChange } from "hooks/useInputChange";
import { useDispatch, useSelector } from "react-redux";
import { InputText } from "components/UI/InputText/InputText";
import { IdOrdersState } from "model/dashboardOrders/dOrdersReducer";
import { numberCurrencyFormat } from "constants/convertToLocalCurrency";
import { SelectButtons } from "components/UI/SelectButtons/SelectButtons";
import { useInputChangeNumberOnly } from "hooks/useInputChangeNumberOnly";
import { getProductItemByBarcode } from "model/dashboardProducts/dProductsAPI";
import { BarcodeSearch } from "components/containers/dashboard/dataOrders/BarcodeSearch";
import {
  addNotificationAC,
  dashboardLoaderAC,
} from "model/system/systemActionCreators";
import {
  dOrdersSetFilteredProductsAC,
  dOrdersSetDiscountInCartItemAC,
  dOrdersSetServicePaymentInCartItemAC,
} from "model/dashboardOrders/dOrdersActionCreators";

export type typeCarts = {
  setOpenChangePrise: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenPopupDeleteMarkedProduct: React.Dispatch<
    React.SetStateAction<boolean>
  >;
};

const MONEY = "money";
const PERCENT = "percent";

const DISCOUNT_TYPES = [
  { id: PERCENT, title: <Trans>%</Trans> },
  { id: MONEY, title: <Trans>sum</Trans> },
];

export const Carts: React.FC<typeCarts> = ({
  setOpenChangePrise,
  setOpenPopupDeleteMarkedProduct,
}) => {
  const dispatch = useDispatch();

  const currentCart = useSelector(
    (state: { dOrders: IdOrdersState }) => state.dOrders.newOrder,
  );
  const selectedStore = useSelector(
    (state: { dOrders: IdOrdersState }) => state.dOrders.selectedStore,
  );

  const {
    value: discountValue,
    setValue: setDiscountValue,
    changeValueHandler: discountChangeValueHandler,
  } = useInputChange("0");

  const [discountVariantId] = useState<string | null>(PERCENT);

  const {
    value: serviceValue,
    setValue: setServiceValue,
    changeValueHandler: serviceChangeValueHandler,
  } = useInputChangeNumberOnly("0");
  const [servicePaymentVariantId, setServicePayment] = useState<string | null>(
    PERCENT,
  );

  const [validationObject, setValidationObject] = useState({
    validServiceValue: true,
    validDiscountValue: true,
  });

  useEffect(() => {
    setServiceValue(
      currentCart && currentCart!.servicePaymentAmount
        ? currentCart!.servicePaymentAmount!.isInPercents
          ? (currentCart!.servicePaymentAmount!.value * 100).toString()
          : currentCart!.servicePaymentAmount!.value.toString()
        : "0",
    );
    setServicePayment(
      currentCart && currentCart!.servicePaymentAmount
        ? currentCart!.servicePaymentAmount!.isInPercents
          ? PERCENT
          : MONEY
        : PERCENT,
    );
    setDiscountValue(
      currentCart && currentCart!.discount
        ? (currentCart!.discount!.value * 100).toString()
        : "0",
    );

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (serviceValue.trim().length > 2) {
      setServiceValue(serviceValue.trim().match(/\d{0,12}.\d{0,2}/)![0]);
    }
    if (
      +serviceValue.trim() < 0 ||
      !serviceValue.trim().match(/^[0-9]*[.,]?[0-9]+$/) ||
      (+serviceValue.trim() > 100 && servicePaymentVariantId === PERCENT)
    ) {
      setValidationObject((v) => {
        return { ...v, validServiceValue: false };
      });
    } else {
      setValidationObject((v) => {
        return { ...v, validServiceValue: true };
      });
    }
    dispatch(
      dOrdersSetServicePaymentInCartItemAC(
        servicePaymentVariantId === PERCENT,
        Number(serviceValue.trim()),
      ),
    );

    // eslint-disable-next-line
  }, [servicePaymentVariantId, serviceValue]);

  useEffect(() => {
    if (discountValue.trim().length > 2) {
      setDiscountValue(discountValue.trim().match(/\d{0,12}.\d{0,2}/)![0]);
    }

    if (
      +discountValue.trim() < 0 ||
      +discountValue.trim() > 100 ||
      !discountValue.trim().match(/^[0-9]*[.,]?[0-9]+$/)
    ) {
      setValidationObject((v) => {
        return { ...v, validDiscountValue: false };
      });
    } else {
      setValidationObject((v) => {
        return { ...v, validDiscountValue: true };
      });
    }
    dispatch(
      dOrdersSetDiscountInCartItemAC(
        discountVariantId === PERCENT,
        Number(discountValue.trim()),
      ),
    );

    // eslint-disable-next-line
  }, [discountValue]);

  let currentTotalPrice: string = "0";
  let totalCount: string = "0";

  if (currentCart && currentCart.productList.length > 0) {
    const price = currentCart.productList
      .reduce((acc, cur) => cur.amount * +cur.price + acc, 0)
      .toFixed(2);

    const service =
      servicePaymentVariantId === PERCENT
        ? (Number(price) * Number(serviceValue.trim())) / 100
        : Number(serviceValue.trim());
    const discount =
      discountVariantId === PERCENT
        ? ((Number(price) + service) * Number(discountValue.trim())) / 100
        : Number(discountValue.trim());

    currentTotalPrice = (Number(price) + service - discount).toFixed(2);

    totalCount = currentCart.productList
      .reduce((acc, cur) => {
        const isDecimalUnits =
          "isDecimalUnits" in cur.product
            ? cur.product.isDecimalUnits
            : !cur.product.integerUnit;
        return !isDecimalUnits ? cur.amount + acc : 1 + acc;
      }, 0)
      .toFixed(0);
  }

  const onBarcodeSearchHandler = async (barcode: string) => {
    dispatch(dashboardLoaderAC(true));

    const response = await getProductItemByBarcode({ barcode });

    if (response.status === 200 && response.data?.content?.length > 0) {
      const selectStoreWithPrice = response.data.content[0].stores.find(
        (item: any) => item.store.id === selectedStore!.id,
      );

      if (!selectStoreWithPrice) {
        dispatch(
          addNotificationAC({
            id: new Date().getTime().toString(),
            message: "ERROR_PRODUCT_NOT_FOUND",
          }),
        );

        dispatch(dashboardLoaderAC(false));

        return;
      }

      // if products in response > 0 , add response.data in products list
      dispatch(dOrdersSetFilteredProductsAC(response.data.content));
    } else {
      dispatch(
        addNotificationAC({
          id: new Date().getTime().toString(),
          message: "ERROR_PRODUCT_NOT_FOUND",
        }),
      );
    }

    dispatch(dashboardLoaderAC(false));
  };

  return (
    <div className="h-full flex flex-col gap-2 sm:gap-4">
      <div className="flex flex-col gap-2 sm:gap-4 flex-grow">
        <BarcodeSearch onSearch={onBarcodeSearchHandler} />

        <div className="relative flex-grow">
          <div className="absolute inset-0 overflow-y-auto flex flex-col gap-2">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-2 py-1 text-left text-xs text-gray-500 uppercase tracking-wider"
                  >
                    <Trans>N%</Trans>
                  </th>
                  <th
                    scope="col"
                    className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    <Trans>Name</Trans>
                  </th>
                  <th
                    colSpan={2}
                    scope="col"
                    className="w-min px-2 py-1 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    <Trans>Count</Trans>
                  </th>
                  <th
                    colSpan={2}
                    scope="col"
                    className="w-min px-2 py-1 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    <Trans>Price</Trans>
                  </th>
                  <th
                    scope="col"
                    className="w-min px-2 py-1 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    <Trans>Del</Trans>
                  </th>
                </tr>
              </thead>

              <tbody>
                {currentCart && currentCart.productList.length > 0 ? (
                  currentCart.productList.map((item, index) => {
                    const isDecimalUnits =
                      "isDecimalUnits" in item.product
                        ? item.product.isDecimalUnits
                        : !item.product.integerUnit;
                    const unitMeasurement =
                      "unitMeasurement" in item.product
                        ? item.product.unitMeasurement
                        : item.product.units;
                    return (
                      <ProductItem
                        key={item.product.name + index}
                        index={index}
                        id={item.product.id}
                        name={item.productName || item.product.name}
                        unit={unitMeasurement}
                        isDecimalUnits={isDecimalUnits}
                        count={item.amount}
                        marked={item.marked}
                        markLabel={
                          item.labels?.length! > 0 ? item.labels![0] : undefined
                        }
                        price={item.price.toString()}
                        setOpenChangePrise={setOpenChangePrise}
                        setOpenPopupDeleteMarkedProduct={
                          setOpenPopupDeleteMarkedProduct
                        }
                      />
                    );
                  })
                ) : (
                  <tr className={""}>
                    <td
                      colSpan={6}
                      className={"text-center text-gray-500 py-16 "}
                    >
                      <Trans>Click on a product to add</Trans>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="flex flex-row flex-wrap  gap-2 justify-between">
        <div className={"flex gap-x-2"}>
          <div className="flex gap-2 flex-col justify-end">
            <div className="flex gap-x-2  text-sm font-medium text-gray-700">
              <div className={"w-30 self-end"}>
                <Trans>Discount</Trans>, %
              </div>
            </div>
            <div className={"w-48"}>
              <InputText
                id="discount"
                name="discount"
                type="number"
                value={discountValue}
                onChange={discountChangeValueHandler}
                labelClassName="text-left"
                minValue={0}
                maxValue={100}
                errorMessage={
                  !validationObject.validDiscountValue ? (
                    <Trans>Enter a value from 0 to 100</Trans>
                  ) : undefined
                }
              />
            </div>
          </div>
          <div className="flex gap-2 flex-col  justify-end">
            <div className="flex whitespace-nowrap w-min gap-x-2  text-sm font-medium text-gray-700  justify-end">
              <div className={"w-30 self-end"}>
                <Trans>Service payment </Trans>,
              </div>
              <SelectButtons
                dataList={DISCOUNT_TYPES}
                checkedId={servicePaymentVariantId}
                setCheckedId={setServicePayment}
                iconClassName={"p-1 w-2 h-2"}
              />
            </div>
            <div className={"flex-grow"}>
              <InputText
                id="service"
                name="service"
                value={serviceValue}
                onChange={serviceChangeValueHandler}
                labelClassName="text-left"
                minValue={0}
                errorMessage={
                  !validationObject.validServiceValue ? (
                    <Trans>
                      Enter a value greater than or equal to 0, for % 0-100%
                    </Trans>
                  ) : undefined
                }
              />
            </div>
          </div>
        </div>
        <div className="col-span-2 flex flex-wrap  gap-2 justify-end items-end flex-grow">
          <div className="flex gap-2 items-end">
            <span className="text-gray-400">
              <Trans>Product count:</Trans>
            </span>
            <span className="text-2xl">{totalCount}</span>
          </div>
          <div className="flex gap-2 items-end">
            <span className="text-gray-400">
              <Trans>Amount:</Trans>
            </span>
            <span className="text-2xl">
              {numberCurrencyFormat(+currentTotalPrice)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
